import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router';
import { isEmpty, last, omit, some } from 'lodash';
import { gtmTracker } from 'modules/taboola-common-frontend-modules/gtmTracker';
import {
    matchPathWithoutQuery,
    parseQueryParams,
    queryParamHookFactory,
    safeDeserializeDrawerStack,
    stringifyQueryParams,
} from 'modules/taboola-common-frontend-modules/query-params';
import { PERSISTENCE_TYPE } from 'modules/taboola-common-frontend-modules/storage/persistenceType';
import { FORM_MODES } from '../../config/formModes';
import { BASE_FORM_ROUTE_PATH } from '../../config/routes';
import { PATH_TO_FORM_MODE_MAP } from '../../modules/campaigns/config/routes/mode';
import { REPORT_FORCE_FETCH_FLAG } from '../../modules/campaigns/modules/campaigns-reports/hooks/useReportForceFetchFlag';
import { useMultiLayerDrawerContext } from '../../modules/multi-layer-drawer/components/MultiLayerDrawerProvider/MultiLayerDrawerProvider';
import { useCurrentValueGetter } from '../useCurrentValueGetter';
import { useHistoryNavigate } from '../useHistoryNavigate';

export const DRAWER_STACK = 'drawerStack';

const emptyDrawerStack = [];

const useDrawerStackUrl = queryParamHookFactory(DRAWER_STACK, {
    persist: PERSISTENCE_TYPE.MEMORY,
    defaultValue: emptyDrawerStack,
    deserializer: safeDeserializeDrawerStack,
    serializer: value => encodeURIComponent(JSON.stringify(value)),
});

export const useDrawerStack = () => {
    const [drawerStack] = useDrawerStackUrl();
    const { setEntityData, setForms } = useMultiLayerDrawerContext();
    const location = useLocation();
    const getLocation = useCurrentValueGetter(location);
    const navigate = useHistoryNavigate();

    const navigateToTopLayerPath = useCallback(
        ({ stack, forceFetchFlag = false, module }) => {
            const lastLayer = last(stack)?.path || `/${module}`;

            if (getLocation().pathname === lastLayer) {
                return;
            }

            const [lastLayerPath, lastLayerRawQuery = ''] = lastLayer.split('?');
            const currentParams = parseQueryParams(getLocation().search);
            const lastParams = parseQueryParams(lastLayerRawQuery ? `?${lastLayerRawQuery}` : '');

            const merged = {
                ...omit(currentParams, DRAWER_STACK),
                ...omit(lastParams, DRAWER_STACK),
                [DRAWER_STACK]: stack,
                ...(forceFetchFlag && { [REPORT_FORCE_FETCH_FLAG]: Date.now() }),
            };

            const newSearch = stringifyQueryParams(merged);
            navigate(`${lastLayerPath}${newSearch}`);
            if (isEmpty(stack)) {
                setForms({});
            }
        },
        [getLocation, navigate, setForms]
    );

    const push = useCallback(
        form => {
            if (some(drawerStack, { path: form.path })) {
                gtmTracker.trackError(`${form.id} form already exists in drawer stack`);
                return;
            }

            navigateToTopLayerPath({
                stack: [...drawerStack, form],
            });
        },
        [drawerStack, navigateToTopLayerPath]
    );

    const pop = useCallback(
        (data, path = null) => {
            const newStack = [...drawerStack];
            const form = newStack.pop();

            if (path) {
                newStack.push({ path });
            }

            if (data) {
                setEntityData(data);
            }

            const { mode, module } =
                matchPathWithoutQuery(form?.path, {
                    path: BASE_FORM_ROUTE_PATH,
                    exact: false,
                })?.params || {};

            const forceFetchFlag = data && isEmpty(newStack) && PATH_TO_FORM_MODE_MAP[mode] === FORM_MODES.CREATE;

            navigateToTopLayerPath({ stack: newStack, forceFetchFlag, module });
        },
        [drawerStack, setEntityData, navigateToTopLayerPath]
    );

    const isMultiLayerMode = useMemo(() => drawerStack.length > 1, [drawerStack]);

    return { stack: drawerStack, push, pop, isMultiLayerMode };
};
